import format from 'date-fns/format';

import { type TsRestClient } from '@eluve/api-client-provider';
import {
  OPTIONAL_HEADERS,
  type PatientImportEditableField,
  type PatientImportIssue,
  type PatientImportJobRow,
  REQUIRED_HEADERS,
} from '@eluve/api-contract';
import { parseFlexibleDate } from '@eluve/date-utils';

export const PATIENT_IMPORT_ROUTE = '/admin/patient-imports';

export const errMsg = (err: unknown): string =>
  err instanceof Error ? err.message : String(err);

// Display DOB as US MM/DD/YYYY, not the stored ISO. MM/dd/yyyy is an accepted
// inline-edit input format, so the displayed value still round-trips on save.
export const formatDobForDisplay = (raw: string | null): string => {
  if (!raw) return '';
  const parsed = parseFlexibleDate(raw);
  if (Number.isNaN(parsed.getTime())) return raw;
  return format(parsed, 'MM/dd/yyyy');
};

export const displayValueForField = (
  field: PatientImportEditableField,
  raw: string | null,
): string =>
  field === 'date_of_birth' ? formatDobForDisplay(raw) : (raw ?? '');

const PAGE_SIZE = 500;
// Defensive cap so a misreporting backend can't spin us forever.
const HARD_CAP = 50000;

export const fetchAllImportRows = async (
  apiClient: TsRestClient,
  tenantId: string,
  jobId: string,
): Promise<{
  rows: PatientImportJobRow[];
  issuesByRowId: Record<string, PatientImportIssue[]>;
}> => {
  const rows: PatientImportJobRow[] = [];
  const issuesByRowId: Record<string, PatientImportIssue[]> = {};
  let offset = 0;
  while (offset < HARD_CAP) {
    const res = await apiClient.patientImport.listImportRows({
      params: { tenantId, jobId },
      query: { limit: PAGE_SIZE, offset },
    });
    if (res.status !== 200) {
      throw new Error(`Failed to load rows (${res.status})`);
    }
    const { body } = res;
    rows.push(...body.rows);
    Object.assign(issuesByRowId, body.issuesByRowId);
    if (rows.length >= body.totalCount || body.rows.length === 0) break;
    offset += body.rows.length;
  }
  return { rows, issuesByRowId };
};

export const triggerCsvDownload = (csv: string, filename: string): void => {
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

export const TEMPLATE_FILENAME = 'patients_template.csv';

const TEMPLATE_CSV =
  [...REQUIRED_HEADERS, ...OPTIONAL_HEADERS].join(',') + '\n';

export const downloadTemplateCsv = (): void =>
  triggerCsvDownload(TEMPLATE_CSV, TEMPLATE_FILENAME);
